/**
 * UUIDv7 generator (RFC 9562), implementing the `IdPort` contract (ADR-0003).
 *
 * Properties relied on elsewhere in the domain:
 * - 48-bit Unix-millisecond timestamp in the high bits -> canonical lowercase
 *   hyphenated string form sorts lexicographically in creation order.
 * - Strictly monotonic per generator instance, even for IDs generated within
 *   the same millisecond (two taps can land in one ms) -- implemented via a
 *   monotonic random-increment counter seeded per millisecond, one of RFC
 *   9562's sanctioned monotonicity methods.
 * - 74 random bits otherwise -> collision probability negligible for a single
 *   user's devices.
 *
 * No dependency: ~20 lines over `crypto.getRandomValues`, per ADR-0003.
 */

import type { EventId, IdPort } from '../../contracts';

const VERSION_NIBBLE = 0x7;
const VARIANT_BITS = 0b10; // RFC 9562 variant, top two bits of the 8th byte octet.

/**
 * Creates an `IdPort` implementation. One instance = one monotonic counter,
 * matching the "per device" monotonicity requirement (ADR-0003) -- callers
 * should hold a single instance per running app/device.
 */
export function createUuidV7IdPort(): IdPort {
  let lastTimestampMs = -1;
  // 74 random bits split as: 12 bits in rand_a (after the version nibble),
  // 62 bits in rand_b (after the 2-bit variant). We track them together as a
  // single monotonic counter within a millisecond so consecutive IDs in the
  // same ms are strictly increasing.
  let counter = randomCounterSeed();

  function randomCounterSeed(): bigint {
    const bytes = randomBytes(9); // 72 random bits, plenty for a fresh seed.
    let value = 0n;
    for (const byte of bytes) {
      value = (value << 8n) | BigInt(byte);
    }
    // Keep the seed within the 74-bit space we manage below.
    return value & ((1n << 74n) - 1n);
  }

  function nextRawId(): string {
    const nowMs = Date.now();

    if (nowMs > lastTimestampMs) {
      lastTimestampMs = nowMs;
      counter = randomCounterSeed();
    } else {
      // Same millisecond (or a clock regression -- treat as same tick and
      // keep advancing monotonically rather than going backwards).
      counter += 1n;
      // 74 bits max; on the astronomically unlikely overflow, roll to the
      // next millisecond value to preserve monotonicity.
      if (counter >= 1n << 74n) {
        lastTimestampMs += 1;
        counter = randomCounterSeed();
      }
    }

    return formatUuidV7(lastTimestampMs, counter);
  }

  function nextEventId(): EventId {
    return nextRawId() as EventId;
  }

  return { nextEventId, nextRawId };
}

function formatUuidV7(timestampMs: number, counter: bigint): string {
  // rand_a: 12 bits (top 12 bits of `counter`'s 74 bits).
  const randA = Number((counter >> 62n) & 0xfffn);
  // rand_b: 62 bits (bottom 62 bits of `counter`).
  const randB = counter & ((1n << 62n) - 1n);

  const timeHex = timestampMs.toString(16).padStart(12, '0').slice(-12);

  const byte6 = (VERSION_NIBBLE << 4) | ((randA >> 8) & 0x0f);
  const byte7 = randA & 0xff;

  // 8th byte: 2-bit variant + top 6 bits of randB's high 8 bits.
  const randBHex = randB.toString(16).padStart(16, '0').slice(-16);
  const randBTopByte = parseInt(randBHex.slice(0, 2), 16);
  const byte8 = (VARIANT_BITS << 6) | (randBTopByte & 0x3f);
  const restOfRandB = randBHex.slice(2); // 14 hex chars = 7 bytes.

  const hex =
    timeHex +
    byte6.toString(16).padStart(2, '0') +
    byte7.toString(16).padStart(2, '0') +
    byte8.toString(16).padStart(2, '0') +
    restOfRandB;

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  const cryptoObj = getCrypto();
  cryptoObj.getRandomValues(bytes);
  return bytes;
}

/**
 * Resolve a WebCrypto-shaped `getRandomValues` source. Every runtime this
 * code actually ships to (browsers, the installed PWA, Cloudflare Workers)
 * exposes `globalThis.crypto` natively -- this function intentionally does
 * NOT special-case Node here (no `node:crypto` fallback), so the module has
 * no environment-specific branching and no dependency (ADR-0003). Test
 * environments that sandbox away the global (see `vitest.config.ts`'s
 * `environmentOptions`/setup) are responsible for polyfilling it, exactly
 * like they would need to polyfill `fetch` or `indexedDB`.
 */
function getCrypto(): Pick<Crypto, 'getRandomValues'> {
  const globalCrypto = (globalThis as { crypto?: Crypto }).crypto;
  if (globalCrypto && typeof globalCrypto.getRandomValues === 'function') {
    return globalCrypto;
  }
  throw new Error(
    'uuidv7: no crypto.getRandomValues available in this environment',
  );
}
